import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PublicDonationForm } from "@/components/fundraising/PublicDonationForm";

interface ThemeParams {
	primaryColor?: string;
	backgroundColor?: string;
	fontFamily?: string;
	borderRadius?: string;
	parentOrigin?: string;
}

export const Route = createFileRoute("/donate/$slug")({
	component: DonatePage,
	validateSearch: (search: Record<string, unknown>): ThemeParams => ({
		primaryColor: typeof search.primaryColor === "string" ? search.primaryColor : undefined,
		backgroundColor: typeof search.backgroundColor === "string" ? search.backgroundColor : undefined,
		fontFamily: typeof search.fontFamily === "string" ? search.fontFamily : undefined,
		borderRadius: typeof search.borderRadius === "string" ? search.borderRadius : undefined,
		parentOrigin: typeof search.parentOrigin === "string" ? search.parentOrigin : undefined,
	}),
});

const ALLOWED_THEME_KEYS = new Set([
	"primaryColor",
	"backgroundColor",
	"fontFamily",
	"borderRadius",
]);

function DonatePage() {
	const { slug } = Route.useParams();
	const searchTheme = Route.useSearch();
	const [theme, setTheme] = useState<ThemeParams>(searchTheme);

	const parentOrigin = searchTheme.parentOrigin;

	useEffect(() => {
		function handleMessage(event: MessageEvent) {
			// Only accept theme messages from the declared parent origin
			if (parentOrigin && event.origin !== parentOrigin) return;
			if (event.data?.type !== "helm-theme" || !event.data.payload) return;

			// Only merge known theme keys to prevent arbitrary state injection
			const raw = event.data.payload as Record<string, unknown>;
			const safe: Partial<ThemeParams> = {};
			for (const key of ALLOWED_THEME_KEYS) {
				if (key in raw && typeof raw[key] === "string") {
					(safe as Record<string, string>)[key] = raw[key] as string;
				}
			}
			setTheme((prev) => ({ ...prev, ...safe }));
		}
		window.addEventListener("message", handleMessage);

		// Signal ready to parent — fires at mount, before form data has loaded.
		// Only send if parentOrigin is declared (secure cross-window messaging).
		if (window.parent !== window && parentOrigin) {
			window.parent.postMessage({ type: "helm-ready" }, parentOrigin);
		}

		return () => window.removeEventListener("message", handleMessage);
	}, [parentOrigin]);

	const style = {
		"--helm-primary": theme.primaryColor,
		"--helm-bg": theme.backgroundColor,
		"--helm-font": theme.fontFamily,
		"--helm-radius": theme.borderRadius ? `${theme.borderRadius}px` : undefined,
	} as React.CSSProperties;

	const onSuccess = (receipt: { receipt_number: string; amount: number; donor_email?: string }) => {
		// Only post success events when parentOrigin is declared — never use "*"
		if (window.parent !== window && parentOrigin) {
			window.parent.postMessage({
				type: "helm-donation-success",
				payload: {
					receiptNumber: receipt.receipt_number,
					amount: receipt.amount,
				},
			}, parentOrigin);
		}
	};

	return (
		<div style={style}>
			<PublicDonationForm slug={slug} onSuccess={onSuccess} />
		</div>
	);
}
